import { environment } from '../../environments/environment';
import { connectMcpEmbedChannel } from './mcp-embed-channel';
import { isMcpHostOrigin } from './mcp-embed-host';
import { McpBillingWindow } from './mcp-billing-window';
import { registerMcpEmbedRefresh } from './mcp-embed-refresh';
import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { LocalStorageService } from 'angular-web-storage';
import { applyMcpEmbedPresentation, isMcpEmbeddedWindow, MCP_EMBED_HISTORY_KEY, rememberMcpEmbedContext } from './mcp-embed-presentation';
import { mcpEmbedConfig as config } from './mcp-embed.config';

/** MCP beta review: this is the adapter between the existing Angular screens and
 * the independently deployed MCP wrapper. It owns navigation/session lifecycle,
 * not widget edits, provider credentials, or chat prose. Review MCP-EMBED.md first.
 * Returning false from openFromHandoff lets normal web startup proceed; handled
 * embedded failures stay masked instead of opening a second login identity.
 */
@Injectable({ providedIn: 'root' })
export class McpEmbedService {
  private readonly embedded = isMcpEmbeddedWindow();
  blocked = this.embedded;
  status = 'Opening Mango…';
  private sessionEstablished = false;
  plansPending = false;
  private requestedScreen='';
  private loadId='';
  private channelPort:MessagePort|null=null;
  // Lifecycle events contain screen facts, not Mango credentials. New native/web
  // cards use a private port; old HTTPS cards retain an exact-origin fallback.
  private notifyHost(data:unknown):void {
    if(this.channelPort)this.channelPort.postMessage(data);
    else if(this.hostOrigin)window.parent.postMessage(data,this.hostOrigin);
  }
  private readyLoad: string | null = null;
  private readyPending = false;
  private screenFinished = false;
  reportScreenReady(expectedScreen = this.requestedScreen, stillReady: () => boolean = () => true):void {
    if(!this.hostOrigin||!this.requestedScreen||expectedScreen!==this.requestedScreen||
       this.screenFinished||this.readyPending||this.readyLoad===this.loadId||!stillReady())return;
    this.readyPending=true;
    const screen=this.requestedScreen,loadId=this.loadId;
    // Hidden embedded views may suspend animation frames (notably in iOS).
    // Keep the paint delay when available, but never depend on it to reveal the view.
    let sent=false;
    const notify=()=>{
      if(sent)return;
      sent=true;clearTimeout(fallback);this.readyPending=false;
      if(this.hostOrigin&&this.requestedScreen===screen&&this.loadId===loadId&&
         !this.screenFinished&&stillReady()) {
        this.readyLoad=loadId;
        this.notifyHost({type:'mango-screen-ready',screen,loadId});
      }
    };
    const fallback=setTimeout(notify,250);
    requestAnimationFrame(()=>requestAnimationFrame(notify));
  }
  private billingCapability: string | null = null;
  private billingWindow = new McpBillingWindow();

  prepareBillingWindow(): void {
    if (this.embedded && this.sessionEstablished && this.billingCapability) this.billingWindow.prepare();
  }

  cancelBillingWindow(): void { this.billingWindow.cancel(); }

  // The MCP server exchanges a screen-bound capability for a short-lived launch
  // ticket. The wrapper receives that relay URL, not a reusable Stripe session.
  // If the reserved window is blocked, `opened: false` keeps a host-openable CTA.
  async openBilling(destination: {sessionId?: string; portalUrl?: string; management?: boolean}, firstTimeTrial = false): Promise<boolean> {
    if (!this.embedded) return false;
    if (!this.hostOrigin || !this.billingCapability) throw new Error('Open Payments and Plans again from chat.');
    const response = await fetch(config.exchangeOrigin + '/billing/ticket', {method:'POST',credentials:'omit',redirect:'error',cache:'no-store',headers:{'Content-Type':'application/json'},body:JSON.stringify({capability:this.billingCapability,...destination})});
    if (!response.ok) { this.cancelBillingWindow(); throw new Error('Open Payments and Plans again from chat.'); }
    const result = await response.json();
    const target = new URL(result.url);
    if (target.origin !== environment.webAppUrl || target.pathname !== '/' || target.search || !/^#mcpBilling=[A-Za-z0-9_-]{43}$/.test(target.hash)) {
      this.cancelBillingWindow();
      throw new Error('Invalid billing destination');
    }
    const opened = this.billingWindow.open(target.href);
    this.notifyHost({type:'mango-billing-open',url:target.href,billingRef:result.billingRef,opened,checkout:!!destination.sessionId,firstTimeTrial:!!destination.sessionId && firstTimeTrial});
    return true;
  }
  layoutPage = 1;
  private setupDraft: {deviceCode: string; name: string; deviceType: string} | null = null;

  takeSetupDraft() {
    const draft = this.setupDraft;
    this.setupDraft = null;
    return draft;
  }
  private hostOrigin: string | null = null;
  private pendingScreen: { action: string; displayId: string | null } | null = null;

  constructor(private storage: LocalStorageService, private router: Router) {
    rememberMcpEmbedContext();
    applyMcpEmbedPresentation(this.embedded);
    this.router.events.subscribe(event => {
      if (this.embedded && event instanceof NavigationStart) {
        const navigation = this.router.getCurrentNavigation();
        if (navigation) {
          navigation.extras.state = { ...navigation.extras.state, [MCP_EMBED_HISTORY_KEY]: true };
        }
      }
      if (this.embedded && event instanceof NavigationEnd) rememberMcpEmbedContext();
      // Navigation is not readiness: the destination view reports when usable.
      if (this.embedded && event instanceof NavigationEnd && event.urlAfterRedirects.startsWith('/plans-and-payments')) {
        this.plansPending = false;
        document.body.classList.remove('mango-chat-plans-pending');
      }
      if (this.embedded && event instanceof NavigationStart &&
          /^\/(login|signup|forgot-password)(?:[/?#;]|$)/.test(event.url)) {
        this.block('mango-connection-required');
      }
    });
  }

  private block(type: 'mango-session-failed' | 'mango-connection-required'): void {
    this.screenFinished = true;
    this.blocked = true;
    this.pendingScreen = null;
    this.status = type === 'mango-connection-required'
      ? 'Connect or reconnect Mango from chat to log in, create an account, or switch accounts.'
      : 'This Mango screen has expired. Ask chat to reopen it here.';
    if (this.hostOrigin) this.notifyHost({ type });
  }

  get active(): boolean { return this.hostOrigin !== null; }

  private editorWatch: ReturnType<typeof setInterval> | null = null;

  // Retire older editors sharing this storage partition to reduce stale writes.
  // This is a browser coordination guard, not backend concurrency authorization;
  // ownership and revision checks remain the responsibility of the server.
  private activateEditor(session: any, handoffHash: string): boolean {
    const key = 'mango-embedded-editor:' + session.id;
    const current = { issuedAt: session.embedIssuedAt, handoffHash, instanceId: crypto.randomUUID() };
    const retire = () => {
      this.blocked = true;
      this.pendingScreen = null;
      this.status = 'This editor has been replaced. Use the latest editor in chat.';
      if (this.editorWatch) clearInterval(this.editorWatch);
      if (this.hostOrigin) this.notifyHost({ type: 'mango-editor-replaced' });
    };
    const check = () => {
      try {
        const latest = JSON.parse(localStorage.getItem(key) || 'null');
        if (latest && latest.instanceId !== current.instanceId && latest.issuedAt >= current.issuedAt) retire();
      } catch { retire(); }
    };
    try {
      if (!Number.isFinite(current.issuedAt)) throw new Error('Fresh editor required');
      const latest = JSON.parse(localStorage.getItem(key) || 'null');
      if (latest && latest.handoffHash !== handoffHash && latest.issuedAt >= current.issuedAt) {
        retire();
        return false;
      }
      localStorage.setItem(key, JSON.stringify(current));
      window.addEventListener('storage', event => { if (event.key === key) check(); });
      window.addEventListener('focus', check);
      document.addEventListener('visibilitychange', check);
      this.editorWatch = setInterval(check, 1000);
      return true;
    } catch {
      this.block('mango-session-failed');
      return false;
    }
  }

  async openFromHandoff(): Promise<boolean> {
    if (!this.embedded) return false;
    if (window.parent === window) {
      this.block('mango-session-failed');
      return true;
    }

    const url = new URL(window.location.href);
    const code = new URLSearchParams(url.hash.slice(1)).get('handoff');
    // Remove the one-use code before navigation or loading another screen.
    url.hash = '';
    history.replaceState(history.state, '', url.pathname + url.search);
    const channelMode=url.searchParams.get('mcpTransport')==='channel-v1';
    let channelProof:string|undefined;
    if(channelMode){
      try{
        const channel=await connectMcpEmbedChannel(url.searchParams.get('mcpLoadId')||'');
        this.channelPort=channel.port;channelProof=channel.proof;
      }catch{this.status='The editor could not connect to chat. Please reopen it.';return true;}
    }
    const host = channelMode?'channel-v1':url.searchParams.get('hostOrigin');
    if (host && (channelMode||isMcpHostOrigin(host, config.allowedHostOrigins))) this.hostOrigin = host;
    if (host && !this.hostOrigin) {
      this.blocked = true;
      this.status = 'This chat host is not supported. Open Mango in your browser or contact Mango support.';
      return true;
    }
    const screen = url.searchParams.get('screen');
    this.requestedScreen=screen||'';this.loadId=url.searchParams.get('mcpLoadId')||'';
    this.readyLoad=null;this.readyPending=false;this.screenFinished=false;
    this.plansPending = screen === 'plans';
    if (screen === 'plans' && this.hostOrigin) {
      registerMcpEmbedRefresh(() => {
        this.blocked = true;
        this.status = 'Updating Payments and Plans…';
        this.notifyHost({ type: 'mango-screen-refresh', screen: 'plans' });
      });
    }
    document.body.classList.toggle('mango-chat-plans-pending', this.plansPending);
    const displayId = url.searchParams.get('displayId');
    const target = screen && Object.prototype.hasOwnProperty.call(config.screens, screen)
      ? config.screens[screen] : null;
    const exchangeUrl = new URL(config.exchangePath, config.exchangeOrigin);
    if (!code || !/^[A-Za-z0-9_-]{43}$/.test(code) || !host || (!channelMode&&!isMcpHostOrigin(host, config.allowedHostOrigins)) || !target ||
        exchangeUrl.origin !== config.exchangeOrigin ||
        ((target.action === 'layout' || target.action === 'preview' || target.action === 'templates') && !/^[1-9][0-9]*$/.test(displayId || ''))) {
      this.block('mango-session-failed');
      return true;
    }

    let authenticated = false;
    try {
      const digest = async (value: string) => Array.from(new Uint8Array(
        await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)),
      )).map(byte => byte.toString(16).padStart(2, '0')).join('');
      const handoffHash = await digest(code);
      const receiptKey = 'mango-embed-completed-handoff';
      const cachedSession = this.storage.get('userDetails');
      let receipt: any = null;
      try { receipt = JSON.parse(sessionStorage.getItem(receiptKey) || 'null'); } catch {}
      // Chat hosts can mount the same result twice. Only reuse a session that
      // this tab already exchanged for this exact screen and one-use handoff.
      const canResume = receipt?.expires > Date.now() && receipt.handoffHash === handoffHash &&
        receipt.host === host && receipt.screen === screen && receipt.displayId === displayId &&
        typeof cachedSession?.authToken === 'string' && cachedSession.authToken &&
        receipt.sessionHash === await digest(cachedSession.authToken);
      if (canResume) {
        this.billingCapability = cachedSession.billingCapability || null;
        authenticated = true;
        this.sessionEstablished = true;
        this.hostOrigin = host;
        this.blocked = false;
        this.layoutPage = Number.isInteger(receipt.embedPage) && receipt.embedPage > 0 ? receipt.embedPage : 1;
        if (!this.activateEditor({ ...cachedSession, embedIssuedAt: receipt.embedIssuedAt }, handoffHash)) return true;
        this.setupDraft = screen === 'setup' && !receipt.setupCompleted ? receipt.embedSetup || null : null;
        this.pendingScreen = { action: screen === 'setup' && receipt.setupCompleted ? 'displays' : target.action, displayId };
        await this.router.navigateByUrl(target.route);
        return true;
      }
      const response = await fetch(exchangeUrl.href, {
        method: 'POST', credentials: 'omit', redirect: 'error', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, screen, displayId, hostOrigin: channelMode?undefined:host, channelProof }),
      });
      if (!response.ok) throw new Error('Session exchange rejected');
      const session = await response.json();
      if (!session || typeof session.authToken !== 'string' || !session.authToken ||
          typeof session.emailId !== 'string' || !session.emailId || !session.id) {
        throw new Error('Invalid session');
      }
      // Reuse the same authenticated-session format as the normal web app.
      // Existing Angular screens expect the normal userDetails shape. Preserve
      // only editor-generation markers while replacing the embedded session.
      // Review this storage boundary on every new host; do not assume separate
      // browser tabs or all mobile webviews share the same storage partition.
      const editorRecords = Object.keys(localStorage).filter(key => key.startsWith('mango-embedded-editor:'))
        .map(key => [key, localStorage.getItem(key)]);
      this.storage.clear();
      editorRecords.forEach(([key, value]) => { if (value !== null) localStorage.setItem(key, value); });
      this.billingCapability = session.billingCapability || null;
      this.storage.set('userDetails', session);
      sessionStorage.setItem(receiptKey, JSON.stringify({
        handoffHash, host, screen, displayId, embedIssuedAt: session.embedIssuedAt, embedPage: session.embedPage, embedSetup: session.embedSetup, sessionHash: await digest(session.authToken),
        expires: Date.now() + 60000,
      }));
      authenticated = true;
      this.sessionEstablished = true;
      this.hostOrigin = host;
      this.blocked = false;
      this.layoutPage = Number.isInteger(session.embedPage) && session.embedPage > 0 ? session.embedPage : 1;
      if (!this.activateEditor(session, handoffHash)) return true;
      this.setupDraft = screen === 'setup' ? session.embedSetup || null : null;
      this.pendingScreen = { action: target.action, displayId };
      await this.router.navigateByUrl(target.route);
    } catch {
      if (authenticated) {
        this.pendingScreen = null;
        this.blocked = true;
        this.status = 'This screen could not load. Ask chat to reopen the editor here.';
        if (this.hostOrigin) this.notifyHost({ type: 'mango-screen-load-failed' });
        return true;
      }
      // An ordinary login here would switch the web account without switching
      // the connector identity. Require a fresh authenticated handoff instead.
      this.block('mango-session-failed');
    }
    return true;
  }

  // Consume once after My Displays has loaded account/subscription context.
  // Repeated Angular render cycles must not reopen the picker or billing flow.
  takeScreenRequest(): { action: string; displayId: string | null } | null {
    const requested = this.pendingScreen;
    this.pendingScreen = null;
    return requested;
  }

  reportScreenUnavailable(): void {
    this.screenFinished = true;
    if (this.hostOrigin) this.notifyHost({ type: 'mango-screen-unavailable' });
  }

  reportTemplateComplete(displayName: string, skipped = false): void {
    this.screenFinished = true;
    if (this.hostOrigin) this.notifyHost({
      type: 'mango-template-complete', displayName, skipped,
    });
  }

  reportSetupComplete(displayName: string, browserDisplay = false): void {
    this.screenFinished = true;
    if (!this.hostOrigin) return;
    // Chat can remount this same handoff after receiving the completion event.
    // Remember the finished screen so a remount does not restart device setup.
    this.setupDraft = null;
    try {
      const key = 'mango-embed-completed-handoff';
      const receipt = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (receipt?.screen === 'setup') {
        receipt.setupCompleted = true;
        delete receipt.embedSetup;
        sessionStorage.setItem(key, JSON.stringify(receipt));
      }
    } catch { /* An unavailable receipt follows the normal fresh-handoff flow. */ }
    this.notifyHost({
      type: 'mango-display-setup-complete', displayName, browserDisplay,
    });
  }
}
